import type {
  DiscoverySummary,
  DraftState,
  PlannedOrderSettings,
  PrimerEstimate,
  PrimerSideEstimate,
  WizardSampleGroup
} from '../../Model/OrderWizard/OrderWizardDraft.ts'
import type { DetectedSequencingPlatform } from '../../Util/sequencingPlatformDetection.ts'
import { SampleConfigurationTypes } from '../../Generated/SampleConfigurationType.ts'
import type { SummaryOf } from './analysisTelemetry.ts'
import { SHEET_METADATA_FIELDS, SHEET_ROLES } from './analysisTelemetry.ts'
import { isFailedSide } from './estimationCore.ts'
import { sheetMatchOf } from './sheet/sheetMatching.ts'
import type { SheetMatch } from './sheet/sheetMatching.ts'
import { sheetSupportTrigger } from './sheetSupport.ts'
import type { PrimerPastePlan } from './primerPaste.ts'
import type { UploadEvent } from './uploadManager.ts'

/** Whether the draft's files should show their primers: the lab's answer first, then the order's format, then the reads. */
export const expectsPrimers = (draft: DraftState): boolean => {
  if (draft.trimmingAnswer) {
    return draft.trimmingAnswer === 'on'
  }
  const configuration = draft.settings?.sampleConfiguration
  if (configuration && configuration.source !== 'undetermined') {
    return !SampleConfigurationTypes[configuration.value].impliesTrimmed
  }
  return draft.discovery?.trimming === 'UNTRIMMED'
}

/** Untrimmed files on which detection found no registered primer pair: shown to the lab as "nothing found". */
export const primersMissingOnUntrimmed = (draft: DraftState): boolean =>
  draft.discovery?.primers.status === 'none' && expectsPrimers(draft)

export type DetectionPayload = {
  discovery?: DiscoverySummary
  settings?: PlannedOrderSettings
  platforms: Record<string, DetectedSequencingPlatform>
  rejections: Record<string, string>
  groups: WizardSampleGroup[]
}

const discoveryFields = (
  discovery: DiscoverySummary | undefined,
  settings: PlannedOrderSettings | undefined
): Omit<SummaryOf<'detection'>, 'step'> => ({
  platform: discovery?.platform,
  layout: discovery?.layout.value,
  layout_confidence: discovery?.layout.confidence,
  trimming: discovery?.trimming,
  multiplexing: discovery?.multiplexing,
  configuration: discovery?.classifier.configuration,
  confidence: discovery ? (discovery.classifier.confidence ?? ('none' as const)) : undefined,
  primers: discovery?.primers.status,
  files_scanned: discovery?.filesScanned,
  runs: discovery?.runs?.count,
  median_read_length: discovery?.readLengths?.centiles[50],
  sample_configuration: settings?.sampleConfiguration.value,
  configuration_source: settings?.sampleConfiguration.source
})

export const detectionSummary = (payload: DetectionPayload, draft: DraftState | undefined): SummaryOf<'detection'> => ({
  step: 'detection',
  outcome: 'completed',
  classified: !!payload.discovery,
  files: Object.keys(payload.platforms).length,
  rejected: Object.keys(payload.rejections).length,
  samples: payload.groups.length,
  ...discoveryFields(payload.discovery, payload.settings ?? draft?.settings),
  platform: payload.discovery?.platform ?? draft?.discovery?.platform
})

/** The draft's detection as it stands, for a warning raised after an answer rather than a drop. */
export const draftDetectionSummary = (draft: DraftState): SummaryOf<'detection'> => ({
  step: 'detection',
  outcome: 'completed',
  classified: !!draft.discovery,
  samples: Object.keys(draft.groups).length,
  ...discoveryFields(draft.discovery, draft.settings)
})

export const failedDetectionSummary = (files: number | undefined): SummaryOf<'detection'> => ({
  step: 'detection',
  outcome: 'failed',
  files
})

type SideVerdict = 'found' | 'none' | 'ambiguous' | 'failed'

/* `support` stays 0 when the gene primer was found on too few reads, and is the top share when the tags were mixed */
const sideVerdict = (side: PrimerSideEstimate): SideVerdict => {
  if (isFailedSide(side)) {
    return 'failed'
  }
  if (side.primer !== null) {
    return 'found'
  }
  return side.support > 0 ? 'ambiguous' : 'none'
}

const CONFIDENCE_ORDER = ['none', 'low', 'high'] as const

export const estimateSummary = (
  estimates: readonly { key: string; estimate: PrimerEstimate }[],
  draft: DraftState
): SummaryOf<'primer_estimate'> => {
  const summary = {
    step: 'primer_estimate' as const,
    samples: estimates.length,
    forward_found: 0,
    forward_none: 0,
    forward_ambiguous: 0,
    reverse_found: 0,
    reverse_none: 0,
    reverse_ambiguous: 0,
    failed: 0,
    tagged: 0,
    lab_spelling: 0,
    confidence_high: 0,
    confidence_low: 0,
    confidence_none: 0,
    expects_primers: expectsPrimers(draft)
  }
  for (const { estimate } of estimates) {
    const forward = sideVerdict(estimate.forward)
    const reverse = sideVerdict(estimate.reverse)
    if (forward === 'failed' || reverse === 'failed') {
      summary.failed++
      continue
    }
    summary[`forward_${forward}`]++
    summary[`reverse_${reverse}`]++
    if (estimate.forward.prefix || estimate.reverse.prefix) {
      summary.tagged++
    }
    if (estimate.forward.spelledByLab || estimate.reverse.spelledByLab) {
      summary.lab_spelling++
    }
    const lowest = Math.min(CONFIDENCE_ORDER.indexOf(estimate.forward.confidence), CONFIDENCE_ORDER.indexOf(estimate.reverse.confidence))
    summary[`confidence_${CONFIDENCE_ORDER[lowest]}`]++
  }
  return summary
}

/** A batch in which no sample, of those estimated, shows a primer on either side. */
export const estimatesFoundNothing = (summary: SummaryOf<'primer_estimate'>): boolean => {
  const estimated = (summary.samples ?? 0) - (summary.failed ?? 0)
  return estimated > 0 && !summary.forward_found && !summary.reverse_found
}

const VIA: Record<SheetMatch['via'][number], 'file' | 'sample_id' | 'sample_name'> = {
  file: 'file',
  'sample id': 'sample_id',
  'sample name': 'sample_name'
}

const matchMethod = (vias: readonly SheetMatch['via'][number][]) => {
  const distinct = [...new Set(vias)]
  if (distinct.length === 0) {
    return 'none' as const
  }
  return distinct.length > 1 ? ('mixed' as const) : VIA[distinct[0]]
}

export const sheetSummary = (draft: DraftState, outcome: 'loaded' | 'remapped' | 'unreadable'): SummaryOf<'sheet'> => {
  const sheet = draft.sheet
  if (outcome === 'unreadable' || !sheet) {
    return { step: 'sheet', outcome: 'unreadable', support: 'parse-failed' }
  }
  const match = sheetMatchOf(draft)
  return {
    step: 'sheet',
    outcome,
    source: sheet.fileKey.startsWith('paste-') ? 'paste' : 'file',
    roles: SHEET_ROLES.filter((role) => !!sheet.mapping[role]),
    metadata: SHEET_METADATA_FIELDS.filter((field) => !!sheet.mapping.metadata[field]),
    columns: sheet.headers.length,
    rows_total: sheet.rows.length,
    rows_matched: Object.keys(match?.byGroup ?? {}).length,
    rows_ambiguous: match?.ambiguousRows.length ?? 0,
    samples: Object.keys(draft.groups).length,
    match_method: matchMethod(Object.values(match?.via ?? {})),
    support: sheetSupportTrigger(draft)?.stats.reason ?? 'ok'
  }
}

export const pasteSummary = (plan: PrimerPastePlan): SummaryOf<'paste'> => {
  switch (plan.kind) {
    case 'keyed':
      return {
        step: 'paste',
        kind: 'keyed',
        rows_total: plan.total,
        rows_matched: plan.matched,
        primers: plan.primers,
        invalid_cells: plan.invalid.length,
        match_method: VIA[plan.via],
        low_match: plan.lowMatch,
        metadata: !!plan.sheet
      }
    case 'positional':
      return {
        step: 'paste',
        kind: 'positional',
        rows_total: plan.rows,
        rows_matched: plan.edits.length,
        primers: plan.primers,
        invalid_cells: plan.invalid.length,
        rows_left_out: plan.rowsLeftOut,
        cells_left_out: plan.cellsLeftOut,
        header_skipped: plan.headerSkipped
      }
    case 'unreadable':
      return { step: 'paste', kind: 'unreadable', refusal: plan.refusal, rows_total: plan.rows, rows_matched: 0, sheet_like: plan.sheetLike }
  }
}

/** A file's upload once it failed, retried or settled; nothing for progress. */
export const uploadSummary = (event: UploadEvent): SummaryOf<'upload'> | undefined => {
  switch (event.type) {
    case 'retrying':
      return { step: 'upload', outcome: 'retrying', failure: event.failure, http_status: event.httpStatus, retries: event.attempt }
    case 'failed':
      return { step: 'upload', outcome: 'failed', failure: event.failure, http_status: event.httpStatus, retries: event.retries }
    case 'succeeded':
      return { step: 'upload', outcome: 'succeeded', retries: event.retries }
    default:
      return undefined
  }
}
